import React from 'react';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import {
  PaymentServicesFilterType,
  defaultPaymentServicesFilter,
} from '../payment-services-filter.type';

interface PaymentServicesFilterProps {
  filters: PaymentServicesFilterType;
  loading?: boolean;
  onChange: (filters: PaymentServicesFilterType) => void;
  onSearch: () => void;
  onClear?: () => void;
}

export const PaymentServicesFilter: React.FC<PaymentServicesFilterProps> = ({
  filters,
  loading,
  onChange,
  onSearch,
  onClear,
}) => {
  const update = <K extends keyof PaymentServicesFilterType>(
    key: K,
    value: PaymentServicesFilterType[K],
  ) => {
    onChange({ ...filters, [key]: value });
  };

  const handleClear = () => {
    if (onClear) {
      onClear();
      return;
    }
    onChange(defaultPaymentServicesFilter());
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' && !loading) {
      onSearch();
    }
  };

  const styles = {
    container: {
      background: 'white',
      borderRadius: '8px',
      padding: '16px',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)',
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '12px',
    },
    title: {
      fontSize: '1.1rem',
      fontWeight: 600 as const,
      color: '#333',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
      gap: '12px',
    },
    field: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '4px',
    },
    label: {
      fontSize: '0.8rem',
      color: '#555',
    },
    inputFull: {
      width: '100%',
    },
    footer: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      flexWrap: 'wrap' as const,
      gap: '12px',
      borderTop: '1px solid #eee',
      paddingTop: '12px',
    },
    buttonsRow: {
      display: 'flex',
      gap: '8px',
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.title}>סינונים</div>

      <div style={styles.grid}>
        <div style={styles.field}>
          <label style={styles.label}>שם שרות</label>
          <InputText
            value={filters.paymentServiceName ?? ''}
            onChange={(e) =>
              update('paymentServiceName', e.target.value || null)
            }
            onKeyDown={handleKeyDown}
            placeholder="חפש לפי שם"
            style={styles.inputFull}
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>מחיר - מ</label>
          <InputNumber
            value={filters.priceFrom}
            onValueChange={(e) => update('priceFrom', e.value ?? null)}
            onKeyDown={handleKeyDown}
            mode="decimal"
            minFractionDigits={0}
            maxFractionDigits={2}
            min={0}
            placeholder="0"
            inputStyle={{ direction: 'rtl', width: '100%' }}
            style={{ width: '100%' }}
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>מחיר - עד</label>
          <InputNumber
            value={filters.priceTo}
            onValueChange={(e) => update('priceTo', e.value ?? null)}
            onKeyDown={handleKeyDown}
            mode="decimal"
            minFractionDigits={0}
            maxFractionDigits={2}
            min={0}
            placeholder="0"
            inputStyle={{ direction: 'rtl', width: '100%' }}
            style={{ width: '100%' }}
          />
        </div>
      </div>

      <div style={styles.footer}>
        <div style={styles.buttonsRow}>
          <Button
            icon="pi pi-filter-slash"
            label="נקה"
            text
            severity="secondary"
            onClick={handleClear}
            disabled={loading}
          />
          <Button
            icon="pi pi-search"
            label="חפש"
            onClick={onSearch}
            loading={loading}
          />
        </div>
      </div>
    </div>
  );
};
